import { FocusEvent, KeyboardEvent, ReactNode, useRef, useState } from "react";
import { inputClassName } from "../../styles/input";
import { cn } from "../../utils";
import { Button } from "./Button";
import { Input } from "./Input";
import { Popover, PopoverContent, PopoverTrigger } from "./Popover";

export type SearchElement<TValue> = {
  label: string;
  value: TValue | null;
};

type InputComboboxProps<TValue> = {
  className?: string;
  inputDecorator?: ReactNode;
  afterInputDecorator?: ReactNode;
  elementsDecorator?: ReactNode;
  placeholder?: string;
  searchElements: SearchElement<TValue>[];
  inputValue: string;
  onChange: (element: SearchElement<TValue>) => void;
  isError?: boolean;
  disabled?: boolean;
};

export function InputCombobox<TValue>({
  className,
  inputDecorator,
  afterInputDecorator,
  elementsDecorator,
  placeholder,
  searchElements,
  inputValue,
  onChange,
  isError = false,
  disabled = false,
}: InputComboboxProps<TValue>) {
  const [open, setOpenState] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
  const listRef = useRef<HTMLUListElement>(null);

  searchElements = searchElements.slice(0, 7);

  const setOpen = (open: boolean) => {
    if (!open) {
      setHighlightedIndex(null);
    }
    setOpenState(open);
  };

  const handleChange = (element: SearchElement<TValue>) => {
    setOpen(true);
    onChange(element);
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    if (listRef.current && !listRef.current.contains(e.relatedTarget as Node)) setOpen(false);
  };

  const handleChoose = (element: SearchElement<TValue>) => {
    handleChange(element);
    setOpen(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    setOpen(true);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prevIndex) =>
        prevIndex === null || prevIndex === searchElements.length - 1 ? 0 : prevIndex + 1,
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prevIndex) =>
        prevIndex === null || prevIndex === 0 ? searchElements.length - 1 : prevIndex - 1,
      );
    } else if (e.key === "Enter" && highlightedIndex !== null) {
      e.preventDefault();
      handleChoose(searchElements[highlightedIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const isOpen = open && searchElements.length > 0;

  return (
    <div className={cn(className)}>
      <Popover open={isOpen} modal={false}>
        <PopoverTrigger className="relative w-full" tabIndex={-1}>
          {inputDecorator && (
            <div className="pointer-events-none absolute inset-y-0 left-2 m-[auto,0] flex items-center hover:cursor-text">
              {inputDecorator}
            </div>
          )}
          <Input
            ref={inputRef}
            value={inputValue}
            onFocus={() => setOpen(true)}
            onBlur={handleBlur}
            onKeyDown={(e) => handleKeyDown(e)}
            onChange={(e) =>
              handleChange({
                label: e.target.value,
                value: null,
              })
            }
            placeholder={placeholder}
            className={cn(
              inputClassName,
              "w-full",
              {
                "ring-destructive focus-visible:ring-destructive focus-visible:ring-offset-background ring-1 focus-visible:ring-offset-1":
                  isError,
                "pl-8": inputDecorator,
              },
              className,
            )}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-controls="search-element-list"
            disabled={disabled}
          />
          {afterInputDecorator && (
            <div className="pointer-events-none absolute inset-y-0 right-2 m-[auto,0] flex items-center hover:cursor-text">
              {afterInputDecorator}
            </div>
          )}
        </PopoverTrigger>
        <PopoverContent
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          align="start"
          className="w-fit max-w-md p-1"
        >
          <ul className="space-y-[2px]" ref={listRef} role="listbox" id="search-element-list">
            {searchElements.map((element, index) => (
              <li
                key={index}
                id={`search-element-${index}`}
                role="option"
                aria-selected={highlightedIndex === index}
              >
                <Button
                  className={cn("size-full justify-start gap-1 px-2", {
                    "bg-accent text-accent-foreground  min-w-fit overflow-visible":
                      highlightedIndex === index,
                  })}
                  variant="null"
                  type="button"
                  onClick={() => handleChoose(element)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  {elementsDecorator}
                  <span className="w-fit truncate text-base font-semibold leading-relaxed text-gray-700">
                    {element.label}
                  </span>
                </Button>
              </li>
            ))}
          </ul>
        </PopoverContent>
      </Popover>
    </div>
  );
}
